//

import React, { Fragment, useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { Divider, MdiIcon, Button } from '@mic3/platform-ui';
import styled from 'styled-components';

import FormGenerator from 'app/containers/Designer/Form/components/FormGenerator';
import FormField from 'app/containers/Designer/Form/components/FormField';
import { connect } from 'react-redux';
import { pick } from 'app/utils/lo/lo';
import { isDefined } from 'app/utils/utils';
import { prepareFunction, enrichContext } from 'app/utils/designer/form/formUtils';
import worker from 'app/utils/worker/worker';

const MdiIconStyled = styled(MdiIcon)`
    line-height: 24px;
`;

const GroupWrapperStyled = styled.div`
    width: 100%;
    position: relative;
`;

const GroupRepeatRemoveButtonStyled = styled(MdiIcon)`
    position: absolute !important;
    right: 5px;
    top: 0px;
    z-index: 1;
    line-height: 16px !important;
    height: 24px !important;
    width: 27px !important;
    cursor: pointer;
`;

const DividerStyled = styled(Divider)`
    margin-top: 8px !important;
    margin-bottom: 8px !important;
`;

const GroupWrapper = styled.div`
    width: 100%;
`;

const fieldify = Component => props => <FormField {...props} Component={Component} />;
const GroupWrapperFieldify = fieldify(GroupWrapper);

const arePropsEqual = (prevProps, nextProps) => {
    if (
        prevProps.newData !== nextProps.newData ||
        prevProps.disabled !== nextProps.disabled ||
        prevProps.addButtons !== nextProps.addButtons ||
        prevProps.name !== nextProps.name ||
        prevProps.index !== nextProps.index ||
        prevProps.component !== nextProps.component ||
        prevProps.context !== nextProps.context ||
        prevProps.onRemove !== nextProps.onRemove ||
        prevProps.onItemChange !== nextProps.onItemChange ||
        prevProps.rowDirection !== nextProps.rowDirection ||
        prevProps.isMobile !== nextProps.isMobile ||
        prevProps.editablePanel !== nextProps.editablePanel ||
        prevProps.editable !== nextProps.editable
    ) {
        return false;
    }
    
    // Deep compare inherited - this is the main source of unnecessary re-renders
    const prevInherited = prevProps.inherited;
    const nextInherited = nextProps.inherited;
    if (prevInherited !== nextInherited) {
        if (
            prevInherited?.data !== nextInherited?.data ||
            prevInherited?.path !== nextInherited?.path ||
            prevInherited?.index !== nextInherited?.index ||
            prevInherited?.changeData !== nextInherited?.changeData
        ) {
            return false;
        }
    }
    
    return true;
};

const GroupRepeaItem = React.memo(React.forwardRef((props, reference) => {
    const {
        rowDirection,
        name,
        index,
        onRemove,
        component,
        onFormTouched,
        onStartChanges,
        onEndChanges,
        newData,
        context,
        onItemChange,
        inherited,
        addButtons,
        disabled,
        editablePanel,
        editable,
        isMobile,
        avoidValidation,
        isValidForm,
    } = props;
    
    const showRemoveButton = addButtons && !disabled;
    
    const inheritedUpdated = useMemo(
        () => ({ ...inherited, index }),
        [index, inherited]
    );

    return (
        <Fragment>
            <GroupWrapperStyled>
                {showRemoveButton && (
                    <GroupRepeatRemoveButtonStyled 
                        onClick={onRemove} 
                        size={20} 
                        name="minus-circle" 
                    />
                )}
                <FormGenerator
                    avoidValidation={avoidValidation}
                    isValidForm={isValidForm}
                    onStartChanges={onStartChanges}
                    onEndChanges={onEndChanges}
                    ref={reference}
                    name={`${name}[${index}]`}
                    components={component.children}
                    data={newData}
                    context={context}
                    rowDirection={!isMobile && !!rowDirection}
                    onChange={onItemChange}
                    onFormTouched={onFormTouched}
                    root={false}
                    inherited={inheritedUpdated}
                    disabled={disabled}
                    editablePanel={editablePanel}
                    editable={editable}
                />
            </GroupWrapperStyled>
        </Fragment>
    );
}), arePropsEqual);

const GroupRepeat = ({ name, addButtons, addDividers, disabled, dataList, changeData, pushSubFormReferences, data, ...restProps }) => {
    const [isDisabled, setIsDisabled] = useState(!!disabled);
    
    // Use refs to store callbacks and avoid recreating them
    const refsMapRef = useRef(new Map());
    const currentDataListRef = useRef(dataList);
    const onChangeCallbacksRef = useRef({});
    const onRemoveCallbacksRef = useRef({});
    
    // Keep dataList ref current
    useEffect(() => {
        currentDataListRef.current = dataList;
    }, [dataList]);
    
    // Compute disabled state
    useEffect(() => {
        const computeDisabled = async () => {
            let result;
            if (!isDefined(disabled) || typeof disabled === 'boolean') {
                result = !!disabled;
            } else {
                result = await worker(
                    await prepareFunction(disabled), 
                    restProps.data || {}, 
                    enrichContext(restProps.context), 
                    pick(restProps.inherited, ['data', 'index', 'path', 'parent'])
                );
            }
            setIsDisabled(result);
        };
        computeDisabled();
    }, [disabled, restProps.data, restProps.context, restProps.inherited]);

    // Stable add callback
    const addRepeated = useCallback(() => {
        if (name) {
            changeData({ name, value: [...(currentDataListRef.current || []), {}] });
        }
    }, [changeData, name]);

    // Get or create onChange callback for item
    const getOnChangeCallback = useCallback((itemIndex, itemName) => {
        const key = `${itemName}-${itemIndex}`;
        if (!onChangeCallbacksRef.current[key]) {
            onChangeCallbacksRef.current[key] = (data, change) => {
                if (!change.name) return Promise.resolve();
                return changeData({
                    name: `${itemName}[${itemIndex}].${change.name}`,
                    value: change.value,
                });
            };
        }
        return onChangeCallbacksRef.current[key];
    }, [changeData]);

    // Get or create remove callback for item
    const getOnRemoveCallback = useCallback((itemIndex) => {
        const key = `${itemIndex}-${name}`;
        if (!onRemoveCallbacksRef.current[key]) {
            onRemoveCallbacksRef.current[key] = () => {
                const currentDataList = currentDataListRef.current;
                if (name && currentDataList && itemIndex >= 0 && itemIndex < currentDataList.length) {
                    const value = [...currentDataList];
                    value.splice(itemIndex, 1);
                    changeData({ name, value });
                }
            };
        }
        return onRemoveCallbacksRef.current[key];
    }, [changeData, name]);

    // Clean up old callbacks when list shrinks
    useEffect(() => {
        const currentSize = dataList?.length || 0;
        Object.keys(onChangeCallbacksRef.current).forEach(key => {
            const idx = parseInt(key.split('-').pop());
            if (idx >= currentSize) delete onChangeCallbacksRef.current[key];
        });
        Object.keys(onRemoveCallbacksRef.current).forEach(key => {
            const idx = parseInt(key.split('-')[0]);
            if (idx >= currentSize) delete onRemoveCallbacksRef.current[key];
        });
    }, [dataList?.length]);

    // Create/get refs for all items
    const refs = useMemo(() => {
        return (dataList || []).map((_, index) => {
            let ref = refsMapRef.current.get(index);
            if (!ref) {
                ref = React.createRef();
                refsMapRef.current.set(index, ref);
            }
            return ref;
        });
    }, [dataList]);
    
    // CRITICAL: Register refs on EVERY render because FormGenerator resets this.subForms = []
    refs.forEach(ref => pushSubFormReferences(ref));

    // Build list items
    const buildList = useMemo(() => {
        const { component, context, inherited, onStartChanges, onEndChanges, onFormTouched, 
            editablePanel, editable, rowDirection, isMobile, avoidValidation, isValidForm } = restProps;
        
        return (dataList || []).map((itemData, index) => {
            const stableKey = itemData?.id ? `item-${itemData.id}` :
                itemData?.uuid ? `item-${itemData.uuid}` :
                `${name}-${index}`;
            
            return (
                <GroupRepeaItem
                    key={stableKey}
                    ref={refs[index]}
                    newData={itemData}
                    index={index}
                    name={name}
                    addButtons={addButtons}
                    disabled={isDisabled}
                    component={component}
                    context={context}
                    inherited={inherited}
                    onStartChanges={onStartChanges}
                    onEndChanges={onEndChanges}
                    onFormTouched={onFormTouched}
                    editablePanel={editablePanel}
                    editable={editable}
                    rowDirection={rowDirection}
                    isMobile={isMobile}
                    avoidValidation={avoidValidation}
                    isValidForm={isValidForm}
                    onRemove={getOnRemoveCallback(index)}
                    onItemChange={getOnChangeCallback(index, name)}
                />
            );
        });
    }, [dataList, name, addButtons, isDisabled, restProps, refs, getOnRemoveCallback, getOnChangeCallback]);
        
    const showAddButton = addButtons && !isDisabled;
    
    return (
        <GroupWrapperFieldify
            data={data}
            isVisible={restProps.component?.properties?.isVisible}
            onLoad={restProps.component?.properties?.onLoad}
            performActions={restProps.inherited.performActions}
        >
            {buildList}
            {showAddButton && (
                <Button variant="outlined" color="secondary" fullWidth onClick={addRepeated}>
                    Add <MdiIconStyled name="plus" />
                </Button>
            )}
            {addDividers && <DividerStyled />}
        </GroupWrapperFieldify>
    );
};

export default connect(state => ({ isMobile: state.global.isMobile }), {})(React.memo(GroupRepeat));
